import React, { useEffect, useState, useRef } from 'react';

import * as Yup from 'yup';
import i18next, { t } from 'i18next';
import { useFormik } from 'formik';

import { Close, Edit, Add } from '@mui/icons-material';
import { EditIcon } from '../../../../assets/images/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../Buttons/Button/Button';
import styles from './EditRoleBasedSkill.module.css';
import BasicPopup from '../../Popups/BasicPopup/BasicPopup';
import RoleBasedSkillsForm from '../../../Forms/Common/RoleBasedSkillsForm/RoleBasedSkillsForm';
import { useGetRoleBasedSkillsHook } from '../../../../utils/utilityHooks';
import {
    processRoleBasedSkillData,
    handleAddRoleBasedSkills,
    handleOptionRemove,
    handleFormSubmit,
    handleDataAndLanguageChanges
} from './EditRoleBasedSkillHelpers';
import { useNavigate } from 'react-router-dom';

function EditRoleBasedSkill() {
    // Redux state
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch();
    // const { t } = useTranslation();
    const currentLanguage = i18next.language;
    const navigate = useNavigate();

    // Local state
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true);
    const [isOpenRoleBasedSkillsPopup, setIsOpenRoleBasedSkillsPopup] = useState(false);
    const [selectedRoleForEdit, setSelectedRoleForEdit] = useState(null);
    const initialLoadDone = useRef(false);

    // Custom hook for options
    const options = useGetRoleBasedSkillsHook();

    // Simple popup handlers without dependencies
    const handlePopupClose = () => {
        setIsOpenRoleBasedSkillsPopup(false);
        setSelectedRoleForEdit(null);
    };
    const handlePopupOpen = () => setIsOpenRoleBasedSkillsPopup(true);

    // Formik setup
    const formik = useFormik({
        initialValues: {
            roleBasedSkills: user.roleBasedSkills || [],
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => {
            handleFormSubmit(values, userToken, dispatch, formik, setEditMode, setFormLoading, t);
        },
    });

    // Load initial data and update on language change
    useEffect(() => {
        handleDataAndLanguageChanges(options, user, initialLoadDone, formik, currentLanguage);
    }, [options, user, currentLanguage]);

    // Handler: Toggle edit mode and reset form if cancelled
    const handleEditToggle = () => {
        processRoleBasedSkillData(user.roleBasedSkills, user, options, formik, currentLanguage);
        setEditMode(prev => !prev);
    };

    // Modified handler function with proper parameters
    const handleOptionRemoveLocal = (roleSkillId) => {
        handleOptionRemove(roleSkillId, formik);
    };

    // Modified handler function with proper parameters
    const handleAddRoleBasedSkillsLocal = (values) => {
        if (selectedRoleForEdit) {
            const existingSkillIndex = formik.values.roleBasedSkills.findIndex(
                roleSkill => roleSkill.role_id === selectedRoleForEdit.role_id
            );

            // Check if this is a new job title that needs to be created
            const isNewJobTitle = !options.find(opt => opt.id === selectedRoleForEdit.role_id);

            if (existingSkillIndex !== -1) {
                // Update existing skills
                const updatedSkills = [...formik.values.roleBasedSkills];
                updatedSkills[existingSkillIndex] = {
                    ...updatedSkills[existingSkillIndex],
                    skills: values.skills
                };
                formik.setFieldValue('roleBasedSkills', updatedSkills);
            } else {
                // Add new role with skills
                const newRoleSkill = {
                    id: Date.now(),
                    role_id: selectedRoleForEdit.role_id,
                    role: selectedRoleForEdit.role_id,
                    role_name_en: selectedRoleForEdit.role_name_en,
                    role_name_ar: selectedRoleForEdit.role_name_ar,
                    skills: values.skills,
                    is_custom: isNewJobTitle // Mark as custom job title
                };

                // If this is a new job title, we need to create it first
                if (isNewJobTitle) {
                    formik.setFieldValue('create_new_title', 'true');
                    formik.setFieldValue('working_title', selectedRoleForEdit.role_name_en);
                }

                formik.setFieldValue('roleBasedSkills', [...formik.values.roleBasedSkills, newRoleSkill]);
            }
        } else {
            handleAddRoleBasedSkills(values, options, formik, currentLanguage, handlePopupClose);
        }
        handlePopupClose();
        setEditMode(false); // Switch to non-edit mode to show save button
    };

    // Handler to open popup for editing a specific role's skills
    const handleEditRoleSkills = (roleSkill) => {
        setSelectedRoleForEdit(roleSkill);
        setIsOpenRoleBasedSkillsPopup(true);
    };

    // Navigate to preferences tab
    const navigateToPreferences = () => {
        localStorage.setItem('selectedProfileTab', '6'); // Set the job preferences tab index
        navigate('/profile/job-preferences');
    };

    // Get job titles from user preferences and filter out empty titles
    const jobTitles = user?.jobPreferences?.[0]?.jobTitles?.filter(title => title && title.trim() !== '') || [];

    // Find matching role options for job titles
    const jobTitleOptions = jobTitles.map(jobTitle => {
        const matchingOption = options.find(opt =>
            opt.label === jobTitle ||
            opt.label_en === jobTitle ||
            opt.label_ar === jobTitle
        );
        return {
            title: jobTitle,
            option: matchingOption
        };
    });

    // Custom rendering of role skills with edit button and job title boxes
    const renderRoleSkillsCustom = (roleSkills) => (
        <div className={styles.roleSkillsGrid}>
            {jobTitleOptions.map(({ title, option }, index) => {
                const matchingRoleSkill = roleSkills.find(roleSkill =>
                    (roleSkill.role_name_en === title || roleSkill.role_name_ar === title)
                );
 
                // Check if this is a custom job title (not in our options database)
                const isCustomTitle = !option && (!matchingRoleSkill || matchingRoleSkill.is_custom);

                return (
                    <div key={index} className={styles.roleSkillContainer}>
                        <div className={styles.roleTitle}>
                            {matchingRoleSkill?.roleName || title}
                            <div className={styles.actionButtons}>
                                {matchingRoleSkill ? (
                                    <Edit
                                        className={styles.editButton}
                                        onClick={() => handleEditRoleSkills(matchingRoleSkill)}
                                        title={t('general.UI.edit_skills_for', { title: title })}
                                    />
                                ) : option ? ( // Only show add button if it's a standard job title
                                    <Add
                                        className={styles.addButton}
                                        onClick={() => {
                                            setSelectedRoleForEdit({
                                                role_id: option.id,
                                                role: option.id,
                                                role_name_en: option.label_en || option.label,
                                                role_name_ar: option.label_ar || option.label,
                                                skills: []
                                            });
                                            setIsOpenRoleBasedSkillsPopup(true);
                                        }}
                                        title={t('general.UI.add')}
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className={styles.skillsList}>
                            {matchingRoleSkill && Array.isArray(matchingRoleSkill.skills) && matchingRoleSkill.skills.length > 0 ? (
                                matchingRoleSkill.skills.map((skill, skillIndex) => (
                                    <span key={skillIndex} className={styles.skillTag}>
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <div className={styles.emptySkillsMessage}>
                                    {isCustomTitle ?
                                        t('custom_job_title_message', 'There are no available skills to select for this job title. You may consider changing the job title to a similar one that allows skill selection.') :
                                        t('general.UI.no_skills_added')
                                    }
                                </div>
                            )}
                            {/* Only show add skill button if it's not a custom job title */}
                            {!isCustomTitle && (
                                <button
                                    className={styles.addSkillButton}
                                    onClick={() => {
                                        if (matchingRoleSkill) {
                                            handleEditRoleSkills(matchingRoleSkill);
                                        } else if (option) {
                                            setSelectedRoleForEdit({
                                                role_id: option.id,
                                                role: option.id,
                                                role_name_en: option.label_en || option.label,
                                                role_name_ar: option.label_ar || option.label,
                                                skills: []
                                            });
                                            setIsOpenRoleBasedSkillsPopup(true);
                                        }
                                    }}
                                    title={t('general.UI.add_skill')}
                                >
                                    <Add style={{ fontSize: '20px' }} />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className={styles.container}>
            <div className={styles.secondColumn}>
                <div className={styles.title}>
                    {t('roleBasedSkills')}
                </div>
                {jobTitles.length === 0 ? (
                    <div className={styles.noDataMessage}>
                        {t('no_job_preferences_yet')} <span className={styles.preferencesLink} onClick={navigateToPreferences}>{t('click_here')}</span> {t('to_go_preferences_tab')}
                    </div>
                ) : (
                    editMode ? (
                        <>
                            <div className={styles.aboutTitle}></div>
                            {renderRoleSkillsCustom(formik.values.roleBasedSkills)}
                        </>
                    ) : (
                        <div className={styles.editContainer}>
                            <div className={styles.aboutTitle}></div>
                            {renderRoleSkillsCustom(formik.values.roleBasedSkills)}
                            <Button
                                onClick={formik.submitForm}
                                className={styles.saveButton}
                            >
                                {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                            </Button>
                        </div>
                    )
                )}
            </div>

            {isOpenRoleBasedSkillsPopup && (
                <BasicPopup
                    isOpen={isOpenRoleBasedSkillsPopup}
                    closePopup={handlePopupClose}
                >
                    <RoleBasedSkillsForm
                        onSubmit={handleAddRoleBasedSkillsLocal}
                        excludeSkills={selectedRoleForEdit ? [] : formik.values.roleBasedSkills.map(rs => rs.role_id || rs.role)}
                        initialRole={selectedRoleForEdit ? (selectedRoleForEdit.role_id || selectedRoleForEdit.role) : ''}
                        initialSkills={selectedRoleForEdit ? selectedRoleForEdit.skills : []}
                        isEditMode={!!selectedRoleForEdit}
                    />
                </BasicPopup>
            )}
            <div className={styles.thirdColumn}>
                {!editMode ? (
                    <Close
                        className={styles.crossIcon}
                        style={{ cursor: 'pointer' }}
                        onClick={handleEditToggle}
                    />
                ) : (
                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(prev => !prev)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                )}
            </div>
        </div>
    );
}

export default EditRoleBasedSkill; 